/**
 * Content addressing — canonical serialization + FNV-1a.
 *
 * This is a CACHE KEY, not a security primitive. The ids are guessable by design: that is
 * exactly how the cache is shared across authors and stories. Nothing may authorize on a
 * hash; the Firestore triggers rehash-and-refuse instead (ENGINEERING §3, §4, §10).
 *
 * Determinism is the whole point — the studio, the function gate, and the Cloud Run
 * renderer must all compute the same id from the same story, on different machines and
 * runtimes. So: no dependencies, no host encoder, no `Intl`-sensitive ordering.
 */
const FNV_PRIME_64 = 1099511628211n;
const FNV_OFFSET_64 = 14695981039346656037n;
/** A distinct basis for the second pass, so the two 64-bit halves are independent. */
const FNV_OFFSET_64_B = 14695981039346656037n ^ 0x9e3779b97f4a7c15n;
const MASK_64 = 0xffffffffffffffffn;
/**
 * UTF-8 encode without `TextEncoder`: the engine compiles with `types: []` and must not
 * assume a DOM or Node global. Lone surrogates become U+FFFD, as a real encoder does.
 */
export function utf8Bytes(input) {
    const out = [];
    for (let i = 0; i < input.length; i += 1) {
        let code = input.charCodeAt(i);
        if (code >= 0xd800 && code <= 0xdbff) {
            const next = i + 1 < input.length ? input.charCodeAt(i + 1) : 0;
            if (next >= 0xdc00 && next <= 0xdfff) {
                code = (code - 0xd800) * 0x400 + (next - 0xdc00) + 0x10000;
                i += 1;
            }
            else {
                code = 0xfffd;
            }
        }
        else if (code >= 0xdc00 && code <= 0xdfff) {
            code = 0xfffd;
        }
        if (code < 0x80) {
            out.push(code);
        }
        else if (code < 0x800) {
            out.push(0xc0 | (code >> 6), 0x80 | (code & 0x3f));
        }
        else if (code < 0x10000) {
            out.push(0xe0 | (code >> 12), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        }
        else {
            out.push(0xf0 | (code >> 18), 0x80 | ((code >> 12) & 0x3f), 0x80 | ((code >> 6) & 0x3f), 0x80 | (code & 0x3f));
        }
    }
    return Uint8Array.from(out);
}
function fnv1a64(bytes, basis, reverse) {
    let acc = basis;
    for (let i = 0; i < bytes.length; i += 1) {
        const byte = bytes[reverse ? bytes.length - 1 - i : i] ?? 0;
        acc = ((acc ^ BigInt(byte)) * FNV_PRIME_64) & MASK_64;
    }
    return acc;
}
/**
 * Canonical JSON: object keys sorted recursively, array order preserved, `undefined`
 * fields dropped. Key order in the input is therefore irrelevant to the address, while
 * array order is significant (scene order changes the picture).
 */
export function canonicalJson(value) {
    if (value === null || value === undefined)
        return 'null';
    const kind = typeof value;
    if (kind === 'string')
        return JSON.stringify(value);
    if (kind === 'boolean')
        return value === true ? 'true' : 'false';
    if (kind === 'number') {
        if (!Number.isFinite(value)) {
            throw new TypeError(`canonicalJson: non-finite number (${String(value)}) is not addressable`);
        }
        // -0 and 0 must not produce different addresses.
        return JSON.stringify(value === 0 ? 0 : value);
    }
    if (kind !== 'object') {
        throw new TypeError(`canonicalJson: ${kind} is not addressable`);
    }
    if (Array.isArray(value)) {
        return `[${value.map((entry) => canonicalJson(entry)).join(',')}]`;
    }
    const record = value;
    const keys = Object.keys(record)
        .filter((key) => record[key] !== undefined)
        .sort();
    const body = keys.map((key) => `${JSON.stringify(key)}:${canonicalJson(record[key])}`).join(',');
    return `{${body}}`;
}
/** FNV-1a, two 64-bit passes → 128 bits, rendered as 32 lowercase hex characters. */
export function hash128(input) {
    const bytes = utf8Bytes(input);
    const high = fnv1a64(bytes, FNV_OFFSET_64, false);
    const low = fnv1a64(bytes, FNV_OFFSET_64_B, true);
    return high.toString(16).padStart(16, '0') + low.toString(16).padStart(16, '0');
}
/**
 * Address any ordered list of parts. The first part is always a DOMAIN TAG, so two
 * different kinds of record with an otherwise equal payload can never collide.
 */
export function hash(...parts) {
    return hash128(canonicalJson(parts));
}
//# sourceMappingURL=hash.js.map